/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Button, Table, Modal, Input, Select, DatePicker, message,
    Tag, Progress, Row, Col, Radio, Alert,
    Tooltip, Divider, Collapse, Spin
} from 'antd';
import {
    EyeOutlined, DownloadOutlined,
    CalendarOutlined, UserOutlined, SaveOutlined, SendOutlined,
    StarFilled, FileExcelOutlined, UploadOutlined, LoadingOutlined,
    CloudUploadOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { exportAssessmentExcel, importAssessmentExcel } from '../../utils/qocAssessmentExcel';
import FacilityService from '../../services/FacilityService';
import util from '../../utils/util';
import moment from 'moment';
import { InstructionsButton, InstructionsModal } from './QoCInstructions';
import CapCommitmentModal from './CapCommitmentModal';
import { usePeriods } from '../../hooks/usePeriods';
import {
    trailAssessmentSubmit, trailCreate, trailUpdate,
    trailAssessmentApprove, TRAIL_MODULES
} from '../../utils/activityTrail';

const { TextArea } = Input;
const { Panel } = Collapse;

// Resolves which reporting period a given date actually falls inside, by
// matching against each period's own period_start/period_end range — NOT
// "whichever period happens to be current right now" (wrong for a late
// audit done after a new period has started) and NOT copied blindly from
// a schedule's own period_label (which may itself be missing on older
// schedules). Falls back to whatever's passed in `fallback` if no period's
// date range contains this date.
function resolvePeriodLabel(dateStr, periodsList, fallback = null) {
    if (!dateStr || !Array.isArray(periodsList) || !periodsList.length) return fallback;
    const d = moment(dateStr);
    if (!d.isValid()) return fallback;
    // ENHEA-prefixed periods are a separate, parallel period type — every
    // other period selector in the platform (QoCSchedules.js, QoCCompliance.js,
    // the Executive Dashboard) explicitly excludes them from normal period
    // pickers, and their date ranges can legitimately overlap a real fiscal
    // period's. Without this exclusion, an assessment could non-deterministically
    // get labeled with an ENHEA period instead of the actual reporting period
    // it belongs to.
    const match = periodsList.find(p =>
        p.period_start && p.period_end && !String(p.label || '').startsWith('ENHEA') &&
        d.isSameOrAfter(moment(p.period_start), 'day') &&
        d.isSameOrBefore(moment(p.period_end), 'day')
    );
    return match ? match.label : fallback;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CLAUSE_COLORS = {
    AACC:'#0ea5e9', COP:'#8b5cf6', MOM:'#f59e0b', PRPE:'#10b981',
    IPCM:'#ef4444', PSQI:'#3b82f6', GROM:'#6366f1', FMS:'#ec4899',
    HRE:'#14b8a6',  ISM:'#a855f7',
};
const RATING = [
    { min:98,  max:100,  label:'Outstanding',          stars:5, color:'#389e0d' },
    { min:95,  max:97.9, label:'Excellent',            stars:4, color:'#1890ff' },
    { min:90,  max:94.9, label:'Good',                 stars:3, color:'#52c41a' },
    { min:85,  max:89.9, label:'Fair',                 stars:2, color:'#faad14' },
    { min:70,  max:84.9, label:'Requires Improvement', stars:1, color:'#fa8c16' },
    { min:0,   max:69.9, label:'Inadequate',           stars:0, color:'#f5222d' },
];
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];

function Stars({ count, size=12 }) {
    return <span>{[1,2,3,4,5].map(i=>(
        <StarFilled key={i} style={{ color: i<=count?'#faad14':'#e8e8e8', fontSize:size }}/>
    ))}</span>;
}

const STATUS_CFG = {
    scheduled:   { color:'#1890ff', label:'Scheduled',   icon:'📅' },
    in_progress: { color:'#faad14', label:'In Progress', icon:'🔄' },
    completed:   { color:'#52c41a', label:'Completed',   icon:'✅' },
    overdue:     { color:'#f5222d', label:'Overdue',     icon:'⚠'  },
    cancelled:   { color:'#999',    label:'Cancelled',   icon:'✗'  },
};

// ─── ME Clause Assessment Form ────────────────────────────────────────────────
function ClauseAssessmentModal({ schedule, onClose, onSaved, viewOnly = false }) {
    const [mes, setMes]               = useState([]);
    const [responses, setResponses]   = useState({});
    const [scores, setScores]         = useState({});
    const [loading, setLoading]       = useState(false);
    // Permission checks
    const canAdd    = util.canAdd('qoc_assess');
    const canEdit   = util.canEdit('qoc_assess');
    const canDelete = util.canDelete('qoc_assess');
    const canExport = util.canExport('qoc_assess');
    const [saving, setSaving]         = useState(false);
    const [assessmentId, setAssessmentId] = useState(null);
    const [assessmentDate, setAssessmentDate] = useState(moment());
    const [showInstructions, setShowInstructions] = useState(false);
    const [activePanel, setActivePanel] = useState([]);
    // Tracks, per ME id, whether the current fulfillment value came from a
    // manual radio click (false) vs. is still eligible for the checklist to
    // auto-fill (true/undefined). Set to false the instant the auditor picks
    // a Score themselves — checking more boxes afterward then never
    // silently changes their answer again for that ME.
    const [autoSuggested, setAutoSuggested] = useState({});
    const [importing, setImporting]   = useState(false);
    const importInputRef              = useRef(null);
    const [lockStatus, setLockStatus] = useState(null);  // { sessions:[], offline_locked_by, offline_locked_by_name }
    const heartbeatRef                = useRef(null);
    const currentUserId               = parseInt(util.getUserId?.() || '0');
    const isLockedByOther = lockStatus?.offline_locked_by &&
                            parseInt(lockStatus.offline_locked_by) !== currentUserId;
    const isAdminUser                 = util.getModules?.()?.qoc_admin === 1 ||
                                        parseInt(localStorage.getItem('is_admin') || '0') === 1;
    const { periods: periodsList }    = usePeriods();

    const clauseCode  = schedule?.clause_code;
    // Treat offline-locked-by-other as viewOnly — no capturing until imported back
    // Note: isLockedByOther is used directly in JSX; viewOnly prop is not overridden here
    // so the lock owner can still import back
    const effectiveViewOnly = viewOnly || !!isLockedByOther;
    const clauseColor = CLAUSE_COLORS[clauseCode] || '#1890ff';

    // ── Auto-save draft to localStorage ──────────────────────────────────────
    // Key is unique per schedule so different assessments don't collide.
    const draftKey = schedule?.id ? `qoc_draft_${schedule.id}` : null;

    // On modal open: check for a saved draft and offer to restore it
    useEffect(() => {
        if (!draftKey || viewOnly) return;
        try {
            const saved = localStorage.getItem(draftKey);
            if (!saved) return;
            const draft = JSON.parse(saved);
            const hasData = Object.values(draft.responses || {}).some(r => r.fulfillment);
            if (!hasData) return;
            const savedAt = draft.savedAt ? new Date(draft.savedAt).toLocaleTimeString() : 'earlier';
            Modal.confirm({
                title: 'Restore unsaved draft?',
                icon: null,
                content: (
                    <div>
                        <div style={{ marginBottom:8 }}>
                            You have unsaved responses from <strong>{savedAt}</strong> for this assessment.
                        </div>
                        <div style={{ fontSize:12, color:'#888' }}>
                            Restore them to continue where you left off, or discard to start fresh.
                        </div>
                    </div>
                ),
                okText: 'Restore draft',
                cancelText: 'Discard',
                onOk: () => {
                    setResponses(draft.responses || {});
                    if (draft.assessmentDate) setAssessmentDate(moment(draft.assessmentDate));
                    message.success('Draft restored');
                },
                onCancel: () => { localStorage.removeItem(draftKey); },
            });
        } catch (e) { /* ignore malformed draft */ }
    }, [draftKey]); // eslint-disable-line

    // Auto-save responses to localStorage on every change (debounced 1.5s)
    const autoSaveTimer = useRef(null);
    useEffect(() => {
        if (!draftKey || viewOnly || !mes.length) return;
        const hasData = Object.values(responses).some(r => r.fulfillment);
        if (!hasData) return;
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => {
            try {
                localStorage.setItem(draftKey, JSON.stringify({
                    responses,
                    assessmentDate: assessmentDate?.toISOString(),
                    savedAt: new Date().toISOString(),
                }));
            } catch (e) { /* storage unavailable */ }
        }, 1500);
        return () => clearTimeout(autoSaveTimer.current);
    }, [responses, assessmentDate]); // eslint-disable-line

    // Clear draft after successful save
    const clearDraft = () => { if (draftKey) localStorage.removeItem(draftKey); };

    useEffect(() => {
        if (!schedule) return;
        loadData();
        if (!viewOnly && schedule.assessment_id) {
            fetchLockStatus(schedule.assessment_id);
            startHeartbeat(schedule.assessment_id);
        }
        return () => stopHeartbeat();
    }, [schedule]); // eslint-disable-line

    const loadData = async () => {
        setLoading(true);
        try {
            const meRes  = await QoCService.getApplicableMEs({
                clause_code: clauseCode,
                facility_id: schedule?.facility_id
            });
            const allMes = meRes.data?.data || [];
            // Only show applicable MEs — non-applicable are hidden completely
            // Scoring formula (B=100/A) already handles variable ME counts correctly
            const meList = allMes.filter(me => me.applicable !== 0);
            setMes(meList);

            if (schedule.assessment_id) {
                setAssessmentId(schedule.assessment_id);
                const respRes = await QoCService.getAssessmentResponsesME(schedule.assessment_id);
                const respMap = {};
                const initialAutoSuggested = {};
                (respRes.data?.data || []).forEach(r => {
                    // checklist_state comes back as a JSON string from the
                    // TEXT column — parse defensively, never let a malformed
                    // value break loading the rest of the assessment.
                    let checklistState = [];
                    if (r.checklist_state) {
                        try {
                            const parsed = JSON.parse(r.checklist_state);
                            if (Array.isArray(parsed)) checklistState = parsed.map(Number);
                        } catch (e) { /* ignore malformed */ }
                    }
                    respMap[r.me_id] = {
                        fulfillment:                r.fulfillment || '',
                        comment:                    r.comment || '',
                        general_observation:        r.general_observation || '',
                        criticality_of_observation: r.criticality_of_observation || '',
                        area_of_good_practice:      r.area_of_good_practice || '',
                        evidence_notes:             r.evidence_notes || '',
                        checklist_state:            checklistState,
                    };
                    // A fulfillment already saved from a past session is a
                    // recorded decision, not a live suggestion — don't let
                    // idly re-checking a box on reopen silently change it.
                    if (r.fulfillment) initialAutoSuggested[r.me_id] = false;
                });
                setResponses(respMap);
                setAutoSuggested(initialAutoSuggested);
                if (schedule.assessment_date) setAssessmentDate(moment(schedule.assessment_date));
            }
            // Default to fully expanded — the auditor should see every ME's
            // full text and inputs immediately, not click through one at a time.
            if (meList.length > 0) setActivePanel(meList.map(me => me.id?.toString()));
        } catch (e) {
            message.error('Failed to load clause data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (mes.length > 0) recalculate(responses); }, [mes, responses]);

    // ── Lock helpers ──────────────────────────────────────────────────────────
    const fetchLockStatus = async (aId) => {
        if (!aId) return;
        try {
            const res = await QoCService.getAssessmentLock(aId);
            setLockStatus(res.data?.data || null);
        } catch (e) { /* silent */ }
    };

    const startHeartbeat = (aId) => {
        if (!aId) return;
        const doHeartbeat = () => {
            QoCService.heartbeatAssessment(aId).then(r => {
                const data = r.data?.data || null;
                setLockStatus(data);
            }).catch(() => {});
        };
        doHeartbeat();
        heartbeatRef.current = setInterval(doHeartbeat, 30000);
    };

    const stopHeartbeat = () => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    };

    const handleBreakLock = async () => {
        if (!assessmentId) return;
        try {
            await QoCService.clearOfflineLock(assessmentId);
            await QoCService.heartbeatAssessment(assessmentId).then(r => setLockStatus(r.data?.data || null));
            message.success('Offline lock cleared');
        } catch (e) { message.error('Failed to break lock'); }
    };

    const setResp = (meId, field, value) => {
        setResponses(prev => ({ ...prev, [meId]: { ...(prev[meId]||{}), [field]: value } }));
    };

    // ── Expand / Collapse all ─────────────────────────────────────────────────
    const allExpanded = mes.length > 0 && activePanel.length === mes.length;
    const toggleExpandAll = () => {
        setActivePanel(allExpanded ? [] : mes.map(me => me.id?.toString()));
    };

    // Same 90/50/49 thresholds used everywhere else (recalculate(), the
    // offline Excel round-trip) — one rule, never allowed to drift.
    const suggestFromChecklist = (checkedCount, totalCount) => {
        if (totalCount <= 0) return '';
        const pct = (checkedCount / totalCount) * 100;
        if (pct >= 90) return 'Met';
        if (pct >= 50) return 'Partially Met';
        return 'Not Met';
    };

    const toggleChecklistItem = (meId, itemId, totalCount) => {
        const numItemId = Number(itemId); // normalize — item.id can arrive as a
        // string from the API while checklist_state is loaded as numbers;
        // without this, toggling silently duplicates rather than removing.
        setResponses(prev => {
            const cur = prev[meId] || {};
            const curChecked = cur.checklist_state || [];
            const nextChecked = curChecked.includes(numItemId)
                ? curChecked.filter(id => id !== numItemId)
                : [...curChecked, numItemId];
            const next = { ...prev, [meId]: { ...cur, checklist_state: nextChecked } };
            // Only auto-fill the Score while this ME hasn't had a manual
            // radio click yet this session (or ever, per the load-time flag).
            if (autoSuggested[meId] !== false) {
                const suggestion = suggestFromChecklist(nextChecked.length, totalCount);
                if (suggestion) {
                    next[meId] = { ...next[meId], fulfillment: suggestion };
                    setAutoSuggested(p => ({ ...p, [meId]: true }));
                }
            }
            return next;
        });
    };

    const recalculate = (resp) => {
        let C=0, D=0, E=0, NA=0, NC=0;
        mes.forEach(me => {
            const f = resp[me.id]?.fulfillment;
            if      (f === 'Met')            C++;
            else if (f === 'Partially Met')  D++;
            else if (f === 'Not Met')        E++;
            else if (f === 'Not Applicable') NA++;
            else                             NC++;
        });
        const A = C + D + E;
        const B = A > 0 ? 100 / A : 0;
        const score    = A > 0 ? parseFloat(((C*B) + (D*B/2)).toFixed(2)) : 0;
        // NC (Not Checked) means the assessor hasn't responded yet — exclude from completion.
        // Only Met, Partially Met, Not Met, and Not Applicable count as "answered".
        const answered = C + D + E + NA;
        setScores({ C, D, E, NA, NC, A, B, score, answered, total: mes.length,
            pct_complete: mes.length > 0 ? Math.round(answered/mes.length*100) : 0 });
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async (submitStatus = 'draft') => {
        if (submitStatus !== 'draft' && (scores.pct_complete || 0) < 100) {
            const unanswered = (scores.total || 0) - (scores.answered || 0);
            message.warning(
                `Cannot submit — ${unanswered} ME${unanswered !== 1 ? 's' : ''} still unanswered. ` +
                `Complete all ${scores.total} measurable elements before submitting for review.`
            );
            return;
        }
        // Every scored ME (Met included) must carry a comment before this can
        // go to review — the visual "* required" cue on its own never
        // actually blocked submission, so enforce it for real here.
        if (submitStatus !== 'draft') {
            const missingComments = mes.filter(me => {
                const resp = responses[me.id] || {};
                return !!resp.fulfillment && !resp.comment?.trim();
            });
            if (missingComments.length > 0) {
                message.warning(
                    `Cannot submit — ${missingComments.length} ME${missingComments.length !== 1 ? 's' : ''} ` +
                    `missing a required comment: ${missingComments.slice(0, 5).map(m => m.ref_no).join(', ')}` +
                    `${missingComments.length > 5 ? ', …' : ''}`
                );
                return;
            }
        }
        setSaving(true);
        try {
            const dateStr = assessmentDate.format('YYYY-MM-DD');
            const asmtData = {
                facility_id:        schedule.facility_id,
                clause_id:          schedule.clause_id,
                schedule_id:        schedule.id,
                assessment_date:    dateStr,
                // Resolved from the actual assessment date, not "today" or
                // "current period" — see resolvePeriodLabel() above. Falls
                // back to the schedule's own period_label (if any) only
                // when no period's date range actually contains this date.
                period_label:       resolvePeriodLabel(dateStr, periodsList, schedule?.period_label || null),
                overall_percentage: scores.score || 0,
                overall_score:      scores.score || 0,
                status:             submitStatus,
                assessor_id:        util.getUserId?.() || schedule.lead_assessor_id,
                ...(assessmentId ? { id: assessmentId } : {}),
            };
            const saveRes = await QoCService.saveAssessmentForSchedule(asmtData);
            const aId     = saveRes.data?.data?.id || assessmentId;
            setAssessmentId(aId);

            const respArray = mes.map(me => ({
                me_id:                      me.id,
                ref_no:                     me.ref_no,
                fulfillment:                responses[me.id]?.fulfillment || '',
                comment:                    responses[me.id]?.comment || '',
                general_observation:        responses[me.id]?.general_observation || '',
                criticality_of_observation: responses[me.id]?.criticality_of_observation || '',
                area_of_good_practice:      responses[me.id]?.area_of_good_practice || '',
                evidence_notes:             responses[me.id]?.evidence_notes || '',
                checklist_state:            responses[me.id]?.checklist_state || [],
            }));
            await QoCService.saveAssessmentResponsesME(aId, respArray);
            clearDraft();
            if (submitStatus === 'draft') {
                assessmentId
                    ? trailUpdate(TRAIL_MODULES.ASSESSMENTS, 'Assessment', aId, { facilityName: schedule.facility_name, clauseCode })
                    : trailCreate(TRAIL_MODULES.ASSESSMENTS, 'Assessment', aId, { facilityName: schedule.facility_name, clauseCode });
            } else {
                trailAssessmentSubmit(schedule.facility_name, aId, schedule.period_label || '');
            }
            message.success(submitStatus === 'draft' ? 'Draft saved ✓' : 'Assessment submitted for review ✓');
            onSaved && onSaved();
            if (submitStatus !== 'draft') onClose();
        } catch (e) {
            message.error(e.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    // ── Export to Excel ───────────────────────────────────────────────────────
    const handleExcelExport = async () => {
        try {
            const filename = exportAssessmentExcel({
                facility: {
                    id:            schedule.facility_id,
                    business_name: schedule.facility_name,
                },
                schedule,
                assessment:     assessmentId ? { id: assessmentId } : null,
                mes,
                responses,
                assessmentDate: assessmentDate.format('YYYY-MM-DD'),
            });
            // Set offline lock so others know this file is being worked on offline
            if (assessmentId) {
                const fname = typeof filename === 'string' ? filename : `${schedule.facility_name}_${schedule.clause_code}.xlsx`;
                await QoCService.setOfflineLock(assessmentId, { filename: fname });
                await fetchLockStatus(assessmentId);
            }
            message.success('Excel downloaded — assessment locked for offline work. Import back when done.');
        } catch (e) {
            message.error('Export failed: ' + e.message);
        }
    };

    // ── Import from Excel ─────────────────────────────────────────────────────
    const handleExcelImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setImporting(true);
        try {
            // Pass the current assessment context so the parser can validate
            // that the file matches this facility + clause and isn't regressing.
            const context = {
                facility_id:          schedule?.facility_id,
                clause_code:          clauseCode,
                currentAnsweredCount: scores.answered || 0,
            };

            const { responses: imported, meta, errors } = await importAssessmentExcel(file, context);

            // ── Regression sentinel ────────────────────────────────────────
            // The import function embeds a special __REGRESSION__ error when
            // the file has fewer answers than what is already saved online.
            const regressionIdx = errors.findIndex(e => e.startsWith('__REGRESSION__'));
            if (regressionIdx !== -1) {
                const [, fileAnswered, onlineAnswered] = errors[regressionIdx].split(':');
                errors.splice(regressionIdx, 1);     // remove sentinel from display list

                Modal.confirm({
                    title: 'Import may lose data',
                    icon: null,
                    content: (
                        <div>
                            <div style={{ marginBottom:12 }}>
                                The file you are importing contains{' '}
                                <strong style={{ color:'#f5222d' }}>{fileAnswered} scored responses</strong>,
                                but there are already{' '}
                                <strong style={{ color:'#1890ff' }}>{onlineAnswered} responses captured online</strong>.
                            </div>
                            <div style={{ fontSize:12, color:'#888' }}>
                                Importing will overwrite online responses with the Excel values.
                                Responses scored online but left blank in the file will be cleared.
                                Continue?
                            </div>
                        </div>
                    ),
                    okText: 'Yes, import anyway',
                    okButtonProps: { danger: true },
                    cancelText: 'Cancel',
                    width: 480,
                    onOk:    () => { doApplyImport(imported, errors); },
                    onCancel:() => { setImporting(false); },
                });
                return; // wait for confirm — onOk/onCancel will handle setImporting
            }

            doApplyImport(imported, errors);
        } catch (err) {
            message.error('Import failed: ' + err.message);
            setImporting(false);
        }
    };

    // Shared helper — actually merges imported responses into state
    const doApplyImport = (imported, errors) => {
        if (errors.length > 0) {
            Modal.warning({
                title: `Imported with ${errors.length} validation issue(s)`,
                content: (
                    <div style={{ maxHeight:300, overflowY:'auto' }}>
                        {errors.map((err, i) => (
                            <div key={i} style={{ color:'#f5222d', fontSize:12, marginBottom:4 }}>⚠ {err}</div>
                        ))}
                        <div style={{ marginTop:12, color:'#555' }}>
                            Valid rows have been imported. Fix the above and re-import to correct them.
                        </div>
                    </div>
                ),
                width: 520,
            });
        }
        const merged = { ...responses, ...imported };
        setResponses(merged);
        recalculate(merged);
        const answeredCount = Object.values(imported).filter(r => r.fulfillment).length;
        message.success(`Imported ${answeredCount} responses from Excel ✓`);
        // Clear offline lock now that the file has been imported back
        if (assessmentId) {
            QoCService.clearOfflineLock(assessmentId).then(() => fetchLockStatus(assessmentId)).catch(() => {});
        }
        setImporting(false);
    };

    const r = getRating(scores.score || 0);
    const today    = moment().startOf('day');
    const isOverdue = schedule && moment(schedule.scheduled_date).isBefore(today)
        && schedule.status !== 'completed';

    return (
        <>
        <InstructionsModal open={showInstructions} onClose={() => setShowInstructions(false)}/>
        <Modal
            open
            onCancel={() => {
                stopHeartbeat();
                if (assessmentId && !viewOnly) {
                    QoCService.clearOnlineEditing(assessmentId).catch(() => {});
                }
                onClose();
            }}
            width="92vw"
            style={{ top:10, maxWidth:1400 }}
            footer={null}
            destroyOnClose
            title={
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:38, height:38, borderRadius:8, background:clauseColor,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        color:'#fff', fontWeight:900, fontSize:14 }}>
                        {clauseCode}
                    </div>
                    <div>
                        <div style={{ fontWeight:800, fontSize:14 }}>{schedule?.clause_title}</div>
                        <div style={{ fontSize:11, color:'#888' }}>
                            {schedule?.facility_name} •
                            <CalendarOutlined style={{ marginLeft:6, marginRight:3 }}/>
                            Scheduled: {schedule?.scheduled_date ? moment(schedule.scheduled_date).format('DD MMM YYYY') : '—'}
                            {isOverdue && <Tag color="red" style={{ marginLeft:6, fontSize:10 }}>OVERDUE</Tag>}
                        </div>
                    </div>
                    <div style={{ marginLeft:'auto' }}>
                        <InstructionsButton style={{ marginRight:8 }}/>
                    </div>
                </div>
            }
        >
            {loading ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div> : (
            <div style={{ display:'flex', gap:0 }}>

                {/* ── Left: ME list ── */}
                <div style={{ flex:1, overflowY:'auto', maxHeight:'75vh', paddingRight:12 }}>

                    {/* ── Offline lock warning ── */}
                    {lockStatus?.offline_locked_by && lockStatus.offline_locked_by !== currentUserId && (
                        <Alert type="error" showIcon style={{ marginBottom:10 }}
                            message={<span style={{ fontWeight:700 }}>🔒 Locked for offline work</span>}
                            description={
                                <div>
                                    <strong>{lockStatus.offline_locked_by_name}</strong> exported this assessment
                                    to Excel and is working offline.
                                    Do not capture responses online until they import the file back.
                                    {isAdminUser && (
                                        <Button danger size="small" style={{ marginLeft:12 }}
                                            onClick={handleBreakLock}>
                                            Break Lock (Admin)
                                        </Button>
                                    )}
                                </div>
                            }/>
                    )}
                    {lockStatus?.offline_locked_by && lockStatus.offline_locked_by === currentUserId && (
                        <Alert type="warning" showIcon style={{ marginBottom:10 }}
                            message={<span style={{ fontWeight:700 }}>📥 You have this file open offline</span>}
                            description={
                                <div>
                                    You exported this assessment to Excel. Import the file back when done,
                                    or clear the lock if you want to continue online instead.
                                    <Button size="small" style={{ marginLeft:12 }}
                                        onClick={handleBreakLock}>
                                        Clear Lock &amp; Work Online
                                    </Button>
                                </div>
                            }/>
                    )}
                    {/* ── Online editing warning — show ALL active users except self ── */}
                    {!lockStatus?.offline_locked_by && (() => {
                        const others = (lockStatus?.sessions || [])
                            .filter(s => parseInt(s.user_id) !== currentUserId);
                        if (!others.length) return null;
                        return (
                            <Alert type="warning" showIcon style={{ marginBottom:10 }}
                                message={<span style={{ fontWeight:700 }}>
                                    👁 {others.length === 1 ? '1 person' : others.length + ' people'} also editing this online
                                </span>}
                                description={
                                    <div>
                                        {others.map((s, i) => (
                                            <span key={s.user_id}>
                                                <strong>{s.user_name}</strong>
                                                {i < others.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                        {' '}currently {others.length === 1 ? 'has' : 'have'} this assessment open.
                                        Responses may conflict — coordinate before submitting.
                                    </div>
                                }/>
                        );
                    })()}

                    {schedule?.assessment_status === 'revision_requested' && (
                        <Alert type="warning" showIcon style={{ marginBottom:12, borderRadius:8 }}
                            message={<span style={{ fontWeight:700 }}>Revision Requested — please address the notes below and re-submit</span>}
                            description={
                                <div style={{ fontSize:12, marginTop:4, color:'#555' }}>
                                    {schedule?.revision_notes || 'No notes provided by reviewer.'}
                                </div>
                            }/>
                    )}

                    {/* Date row + Excel buttons */}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14,
                        padding:'8px 12px', background:'#f8faff', borderRadius:8,
                        border:'1px solid #e0eaff', flexWrap:'wrap' }}>
                        <CalendarOutlined style={{ color:'#1890ff' }}/>
                        <span style={{ fontSize:12, fontWeight:600 }}>Assessment Date:</span>
                        <DatePicker size="small" value={assessmentDate}
                            onChange={v => setAssessmentDate(v || moment())}
                            disabled={effectiveViewOnly} style={{ width:140 }}/>
                        {schedule?.window_end && (
                            <span style={{ fontSize:11, color:'#888' }}>
                                Window ends: {moment(schedule.window_end).format('DD MMM YYYY')}
                            </span>
                        )}

                        {/* ── Expand / Collapse all — always available, edit or view mode ── */}
                        {mes.length > 0 && (
                            <Button size="small" onClick={toggleExpandAll}>
                                {allExpanded ? 'Collapse All' : 'Expand All'}
                            </Button>
                        )}

                        {/* ── Offline Excel workflow ── */}
                        {!viewOnly && (
                            <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                                <Tooltip title="Download current responses as Excel for offline completion">
                                    <Button
                                        size="small"
                                        icon={<FileExcelOutlined/>}
                                        onClick={handleExcelExport}
                                        disabled={mes.length === 0}
                                        style={{ borderColor:'#52c41a', color:'#52c41a' }}>
                                        Export Excel
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Upload a completed Excel file to import responses">
                                    <Button
                                        size="small"
                                        icon={importing ? <LoadingOutlined/> : <UploadOutlined/>}
                                        loading={importing}
                                        onClick={() => importInputRef.current?.click()}
                                        disabled={mes.length === 0}
                                        style={{ borderColor:'#1890ff', color:'#1890ff' }}>
                                        Import Excel
                                    </Button>
                                </Tooltip>
                                <input
                                    ref={importInputRef}
                                    type="file"
                                    accept=".xlsx"
                                    style={{ display:'none' }}
                                    onChange={handleExcelImport}/>
                            </div>
                        )}
                    </div>

                    {mes.length === 0
                        ? <Alert message="No measurable elements found for this clause." type="warning" showIcon/>
                        : (
                            <Collapse activeKey={activePanel} onChange={setActivePanel} accordion={false} size="small">
                                {mes.map((me) => {
                                    const resp  = responses[me.id] || {};
                                    const f     = resp.fulfillment;
                                    const isKCS = me.is_critical == 1;
                                    const fColor = f === 'Met'            ? '#389e0d'
                                                 : f === 'Partially Met'  ? '#fa8c16'
                                                 : f === 'Not Met'        ? '#f5222d'
                                                 : f === 'Not Applicable' ? '#595959'
                                                 : f === 'Not Checked'    ? '#722ed1'
                                                 : '#ccc';
                                    return (
                                        <Panel
                                            key={me.id?.toString()}
                                            style={{
                                                marginBottom:4, borderRadius:6,
                                                border: isKCS ? '1.5px solid #ffccc7' : '1px solid #f0f0f0',
                                                background: isKCS ? '#fff8f8' : '#fff',
                                            }}
                                            header={
                                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                                    <span style={{ fontFamily:'monospace', fontSize:11,
                                                        color: isKCS ? '#f5222d' : clauseColor, fontWeight:700, flexShrink:0 }}>
                                                        {isKCS && '⚠ '}{me.ref_no}
                                                    </span>
                                                    <span style={{ fontSize:12, flex:1, overflow:'hidden',
                                                        textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                        {me.me_text}
                                                    </span>
                                                    {f && (
                                                        <Tag style={{ background:fColor, color:'#fff',
                                                            border:'none', fontSize:10, flexShrink:0 }}>
                                                            {f === 'Met'            ? '✓ M'
                                                           : f === 'Partially Met'  ? '◑ PM'
                                                           : f === 'Not Met'        ? '✗ NM'
                                                           : f === 'Not Applicable' ? '— NA'
                                                           : '? NC'}
                                                        </Tag>
                                                    )}
                                                </div>
                                            }
                                        >
                                            {/* Full, untruncated question text — the header above is just a
                                                compact label for when a panel is manually collapsed; this is
                                                what's actually meant to be read once expanded (the default). */}
                                            <div style={{ fontSize:13, fontWeight:600, color:'#1a1a2e',
                                                marginBottom:10, lineHeight:1.5 }}>
                                                {me.me_text}
                                            </div>

                                            {/* ── Verification checklist — only for MEs where national office
                                                has reviewed and confirmed a checklist (checklist_reviewed=1,
                                                enforced server-side: the backend simply never sends items for
                                                anything unreviewed). Everything else behaves exactly as before. ── */}
                                            {Array.isArray(me.checklist_items) && me.checklist_items.length > 0 && (() => {
                                                const total = me.checklist_items.length;
                                                const checkedIds = resp.checklist_state || [];
                                                const checkedCount = checkedIds.length;
                                                const pct = total > 0 ? Math.round((checkedCount/total)*100) : 0;
                                                const suggestion = suggestFromChecklist(checkedCount, total);
                                                return (
                                                    <div style={{ marginBottom:12, padding:'10px 12px',
                                                        background:'#f5f3ff', borderRadius:8, border:'1px solid #e9d5ff' }}>
                                                        <div style={{ display:'flex', alignItems:'center',
                                                            justifyContent:'space-between', marginBottom:8, flexWrap:'wrap', gap:4 }}>
                                                            <span style={{ fontWeight:700, fontSize:11, color:'#6d28d9' }}>
                                                                📋 VERIFICATION CHECKLIST
                                                            </span>
                                                            <span style={{ fontSize:11, fontWeight:600, color:'#6d28d9' }}>
                                                                {checkedCount}/{total} checked ({pct}%)
                                                                {suggestion && <> — Suggested: <strong>{suggestion}</strong></>}
                                                            </span>
                                                        </div>
                                                        {me.checklist_items.map(item => {
                                                            const isChecked = checkedIds.includes(Number(item.id));
                                                            return (
                                                                <label key={item.id} style={{ display:'flex',
                                                                    alignItems:'center', gap:8, marginBottom:4,
                                                                    fontSize:12, cursor: effectiveViewOnly ? 'default' : 'pointer' }}>
                                                                    <input type="checkbox" checked={isChecked}
                                                                        disabled={effectiveViewOnly}
                                                                        onChange={() => toggleChecklistItem(me.id, item.id, total)}
                                                                        style={{ width:14, height:14, flexShrink:0 }}/>
                                                                    {item.item_text}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}

                                            <Row gutter={10}>
                                                {/* Score */}
                                                <Col span={5}>
                                                    <div style={{ fontWeight:700, fontSize:11, marginBottom:6 }}>
                                                        Score *
                                                        {isKCS && <Tag color="red" style={{ marginLeft:4, fontSize:9 }}>KCS</Tag>}
                                                    </div>
                                                    <Radio.Group
                                                        value={resp.fulfillment}
                                                        onChange={e => {
                                                            setResp(me.id, 'fulfillment', e.target.value);
                                                            // A deliberate manual pick always wins from here on —
                                                            // further checkbox toggles stop auto-changing this ME.
                                                            setAutoSuggested(p => ({ ...p, [me.id]: false }));
                                                        }}
                                                        disabled={effectiveViewOnly}
                                                        style={{ display:'flex', flexDirection:'column', gap:5 }}>
                                                        <Radio value="Met"            style={{ color:'#389e0d', fontWeight:600, fontSize:12 }}>✓ Met (100%)</Radio>
                                                        <Radio value="Partially Met"  style={{ color:'#fa8c16', fontWeight:600, fontSize:12 }}>◑ Partially Met (50%)</Radio>
                                                        <Radio value="Not Met"        style={{ color:'#f5222d', fontWeight:600, fontSize:12 }}>✗ Not Met (0%)</Radio>
                                                        <Radio value="Not Applicable" style={{ color:'#595959', fontWeight:600, fontSize:12 }}>— Not Applicable</Radio>
                                                        <Radio value="Not Checked"    style={{ color:'#722ed1', fontWeight:600, fontSize:12 }}>? Not Checked</Radio>
                                                    </Radio.Group>
                                                </Col>

                                                {/* Comment */}
                                                <Col span={6}>
                                                    <div style={{ fontWeight:700, fontSize:11, marginBottom:4 }}>
                                                        Comment
                                                        {f && (
                                                            <span style={{ color:'#f5222d', fontSize:10, marginLeft:4 }}>* required</span>
                                                        )}
                                                    </div>
                                                    <TextArea rows={5} value={resp.comment}
                                                        onChange={e => setResp(me.id, 'comment', e.target.value)}
                                                        disabled={effectiveViewOnly}
                                                        placeholder={f ? 'Required — explain finding...' : 'Optional...'}
                                                        style={{ fontSize:11,
                                                            borderColor: f && !resp.comment ? '#ffccc7' : undefined }}/>
                                                </Col>

                                                {/* Observation + criticality */}
                                                <Col span={7}>
                                                    <div style={{ fontWeight:700, fontSize:11, marginBottom:4 }}>General Observation</div>
                                                    <TextArea rows={2} value={resp.general_observation}
                                                        onChange={e => setResp(me.id, 'general_observation', e.target.value)}
                                                        disabled={effectiveViewOnly} placeholder="Additional observation..."
                                                        style={{ fontSize:11, marginBottom:6 }}/>
                                                    <div style={{ fontWeight:700, fontSize:10, color:'#666', marginBottom:3 }}>Criticality</div>
                                                    <Radio.Group value={resp.criticality_of_observation}
                                                        onChange={e => setResp(me.id, 'criticality_of_observation', e.target.value)}
                                                        disabled={effectiveViewOnly} size="small">
                                                        <Radio.Button value="high"   style={{ fontSize:10, color:'#f5222d' }}>High</Radio.Button>
                                                        <Radio.Button value="medium" style={{ fontSize:10, color:'#fa8c16' }}>Medium</Radio.Button>
                                                        <Radio.Button value="low"    style={{ fontSize:10, color:'#389e0d' }}>Low</Radio.Button>
                                                        <Radio.Button value="na"     style={{ fontSize:10 }}>N/A</Radio.Button>
                                                    </Radio.Group>
                                                    <div style={{ fontWeight:700, fontSize:10, color:'#52c41a', marginTop:6, marginBottom:3 }}>
                                                        Area of Good Practice
                                                    </div>
                                                    <TextArea rows={2} value={resp.area_of_good_practice}
                                                        onChange={e => setResp(me.id, 'area_of_good_practice', e.target.value)}
                                                        disabled={effectiveViewOnly} placeholder="Positive observation..."
                                                        style={{ fontSize:11 }}/>
                                                </Col>

                                                {/* Evidence */}
                                                <Col span={6}>
                                                    <div style={{ fontWeight:700, fontSize:11, marginBottom:4 }}>Evidence Notes</div>
                                                    <TextArea rows={4} value={resp.evidence_notes}
                                                        onChange={e => setResp(me.id, 'evidence_notes', e.target.value)}
                                                        disabled={effectiveViewOnly} placeholder="Evidence references..."
                                                        style={{ fontSize:11 }}/>
                                                </Col>
                                            </Row>
                                        </Panel>
                                    );
                                })}
                            </Collapse>
                        )}
                </div>

                {/* ── Right: Score panel ── */}
                <div style={{ width:200, flexShrink:0, paddingLeft:16,
                    borderLeft:'1px solid #f0f0f0', display:'flex', flexDirection:'column', gap:10 }}>

                    <Card size="small" style={{ background:'#f8faff', borderColor:`${clauseColor}30` }}>
                        <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:4 }}>COMPLETION</div>
                        <Progress type="circle" percent={scores.pct_complete||0} width={80}
                            strokeColor={clauseColor}
                            format={v=><span style={{ fontSize:14, fontWeight:900 }}>{v}%</span>}/>
                        <div style={{ fontSize:10, color:'#888', marginTop:6 }}>
                            {scores.answered||0} / {scores.total||0} MEs answered
                        </div>
                    </Card>

                    {(scores.A||0) > 0 && (scores.pct_complete||0) === 100 && (
                        <Card size="small" style={{ background:`${r.color}08`, borderColor:`${r.color}40` }}>
                            <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:4 }}>CLAUSE SCORE</div>
                            <div style={{ fontSize:30, fontWeight:900, color:r.color }}>{scores.score}%</div>
                            <Stars count={r.stars} size={13}/>
                            <div style={{ fontSize:11, fontWeight:700, color:r.color, marginTop:3 }}>{r.label}</div>
                            <Divider style={{ margin:'8px 0' }}/>
                            <div style={{ fontSize:10, color:'#555', fontFamily:'monospace' }}>
                                A={scores.A} B={scores.B?.toFixed(2)}<br/>
                                ✓ {scores.C} × {scores.B?.toFixed(1)} = {((scores.C||0)*(scores.B||0)).toFixed(1)}<br/>
                                ◑ {scores.D} × {scores.B?.toFixed(1)}/2 = {((scores.D||0)*(scores.B||0)/2).toFixed(1)}<br/>
                                — NA:{scores.NA||0} ?NC:{scores.NC||0}
                            </div>
                        </Card>
                    )}

                    <Card size="small">
                        <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:6 }}>ME COUNTS</div>
                        {[
                            { label:'✓ Met',           val:scores.C||0,  color:'#389e0d' },
                            { label:'◑ Partially Met', val:scores.D||0,  color:'#fa8c16' },
                            { label:'✗ Not Met',       val:scores.E||0,  color:'#f5222d' },
                            { label:'— NA',            val:scores.NA||0, color:'#595959' },
                            { label:'? NC',            val:scores.NC||0, color:'#722ed1' },
                        ].map(item=>(
                            <div key={item.label} style={{ display:'flex', justifyContent:'space-between',
                                alignItems:'center', marginBottom:3 }}>
                                <span style={{ fontSize:11, color:item.color, fontWeight:600 }}>{item.label}</span>
                                <Tag style={{ background:item.color, color:'#fff', border:'none',
                                    minWidth:24, textAlign:'center', fontSize:11 }}>{item.val}</Tag>
                            </div>
                        ))}
                    </Card>

                    {!viewOnly && (
                        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            <Button icon={<SaveOutlined/>} loading={saving}
                                onClick={() => handleSave('draft')}
                                style={{ borderColor:clauseColor, color:clauseColor }}>
                                Save Draft
                            </Button>
                            <Tooltip title={
                                (scores.pct_complete||0) < 100
                                    ? `${(scores.total||0)-(scores.answered||0)} ME${((scores.total||0)-(scores.answered||0))!==1?'s':''} unanswered — complete all before submitting`
                                    : null
                            }>
                                <Button type="primary" icon={<SendOutlined/>} loading={saving}
                                    disabled={(scores.pct_complete||0) < 100}
                                    onClick={() => handleSave('submitted')}
                                    style={{
                                        background:  (scores.pct_complete||0) < 100 ? undefined : clauseColor,
                                        borderColor: (scores.pct_complete||0) < 100 ? undefined : clauseColor,
                                    }}>
                                    Submit for Review
                                    {(scores.pct_complete||0) < 100 && (
                                        <span style={{ fontSize:10, marginLeft:4, color:'#f5222d' }}>
                                            ({scores.pct_complete||0}%)
                                        </span>
                                    )}
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
            )}
        </Modal>
        </>
    );
}

// ─── Main Assessment Page ──────────────────────────────────────────────────────
export default function QoCAssessment() {
    const [schedules, setSchedules]       = useState([]);
    const [loading, setLoading]           = useState(false);
    const [selectedSchedule, setSelected] = useState(null);
    const [viewOnly, setViewOnly]         = useState(false);
    const [filters, setFilters]           = useState({ facility_id: null, region: null, status: null, clause_id: null });
    const [facilities, setFacilities]     = useState([]);
    const [clauses, setClauses]           = useState([]);
    const [myView, setMyView]             = useState(true);
    const [hiePushing, setHiePushing]     = useState({});
    const [hiePushAll, setHiePushAll]     = useState(false);
    const [capModalFacility, setCapModalFacility] = useState(null); // { id, business_name } or null

    // ── Bulk export/import for offline capture ──────────────────────────────
    // selectedIds tracks checkbox state across every facility group's own
    // <Table> — a single flat Set, since a schedule's id is unique regardless
    // of which facility card it's rendered under.
    const [selectedIds, setSelectedIds]   = useState(new Set());
    const [bulkExporting, setBulkExporting] = useState(false);
    const [exportingRow, setExportingRow] = useState(null); // schedule id currently exporting solo
    const [importOpen, setImportOpen]     = useState(false);
    const [importing, setImportingBulk]   = useState(false);
    const [importResults, setImportResults] = useState(null); // { submitted:[], draft:[], failed:[] }
    const importBulkInputRef              = useRef(null);

    const isAdmin    = util.getModules?.()?.qoc_admin   === 1;
    const isApprover = util.getModules?.()?.qoc_approve === 1;
    const { periods: periodsList } = usePeriods();

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data||[]));
        QoCService.getClauses().then(r => setClauses(r.data?.data||[]));
        load();
    }, []);

    // ── Offline capture: export / import helpers ─────────────────────────────
    const buildResponsesMap = (rows) => {
        const map = {};
        rows.forEach(r => {
            map[r.me_id] = {
                fulfillment:                r.fulfillment || '',
                comment:                    r.comment || '',
                general_observation:        r.general_observation || '',
                criticality_of_observation: r.criticality_of_observation || '',
                area_of_good_practice:      r.area_of_good_practice || '',
                evidence_notes:             r.evidence_notes || '',
            };
        });
        return map;
    };

    // Shared by both the per-row export icon and the bulk "Export Selected"
    // action — fetches this one schedule's MEs + any existing responses,
    // exports to Excel, and sets the offline lock if there's an assessment
    // to attach it to.
    const exportSingleSchedule = async (schedule) => {
        // A schedule that's never been started ("Start", not "Continue") has
        // no assessment_id yet — and therefore nothing to lock. Bulk export
        // is exactly the situation where several freshly-assigned,
        // never-started schedules get selected together, so skipping the
        // lock for these was the actual reason only some locked in a bulk
        // batch: whichever ones already had an assessment locked
        // correctly, and every brand-new one silently didn't. Fix: create a
        // minimal draft assessment first so there's always a real id to
        // lock against, regardless of whether work has begun yet.
        let assessmentId = schedule.assessment_id;
        if (!assessmentId) {
            const todayStr = moment().format('YYYY-MM-DD');
            const createRes = await QoCService.saveAssessmentForSchedule({
                facility_id: schedule.facility_id,
                clause_id:   schedule.clause_id,
                schedule_id: schedule.id,
                status:      'draft',
                assessment_date: todayStr,
                period_label: resolvePeriodLabel(todayStr, periodsList, schedule?.period_label || null),
                overall_percentage: 0,
                overall_score: 0,
            });
            if (createRes.data?.code !== 200) {
                throw new Error(createRes.data?.message || 'Failed to create draft assessment for export');
            }
            assessmentId = createRes.data?.data?.id;
        }

        const mesRes = await QoCService.getApplicableMEs({
            clause_code: schedule.clause_code,
            facility_id: schedule.facility_id
        });
        const mes = (mesRes.data?.data || []).filter(me => me.applicable !== 0);
        let responses = {};
        if (schedule.assessment_id) {
            const respRes = await QoCService.getAssessmentResponsesME(schedule.assessment_id);
            responses = buildResponsesMap(respRes.data?.data || []);
        }
        const facility = { id: schedule.facility_id, business_name: schedule.facility_name };
        const assessmentDate = schedule.assessment_date
            ? moment(schedule.assessment_date).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD');

        // Lock — a fast, reliable API call, deliberately not gated on
        // whether the actual file download succeeds. Browsers block or
        // silently drop several auto-triggered downloads fired in quick
        // succession (exactly what a bulk export loop does); if locking
        // only happened after a successful download, one blocked download
        // partway through a batch would silently skip locking for every
        // schedule after it.
        //
        // This backend always responds HTTP 200 with a {code, message}
        // body rather than a real HTTP error status, so a failed lock
        // (permission denied, an exception, anything) would otherwise
        // resolve here as if it succeeded. Explicitly checking the response
        // code turns that into a real, visible failure instead.
        if (assessmentId) {
            const fname = `${schedule.facility_name}_${schedule.clause_code || 'FullAudit'}.xlsx`;
            const lockRes = await QoCService.setOfflineLock(assessmentId, { filename: fname });
            if (lockRes.data?.code !== 200) {
                throw new Error(lockRes.data?.message || 'Failed to set offline lock');
            }
        }

        // File generation/download — isolated in its own try/catch so a
        // browser-level download failure never looks like (or causes) the
        // lock itself to have failed; the lock above has already succeeded
        // by this point regardless of what happens here.
        try {
            exportAssessmentExcel({
                facility, schedule,
                assessment: schedule.assessment_id ? { id: schedule.assessment_id } : null,
                mes, responses, assessmentDate,
            });
        } catch (e) {
            throw new Error(`Locked for offline work, but the file itself failed to generate: ${e.message}`);
        }
    };
    const handleExportRow = async (schedule) => {
        setExportingRow(schedule.id);
        try {
            // Create draft assessment first if none exists — prevents duplicate on import
            let scheduleWithAsmt = { ...schedule };
            if (!schedule.assessment_id) {
                const todayStr = moment().format('YYYY-MM-DD');
                const draftRes = await QoCService.saveAssessmentForSchedule({
                    facility_id: schedule.facility_id, clause_id: schedule.clause_id,
                    schedule_id: schedule.id, assessment_date: todayStr,
                    period_label: resolvePeriodLabel(todayStr, periodsList, schedule?.period_label || null),
                    overall_percentage: 0, overall_score: 0, status: 'draft',
                    assessor_id: util.getUserId?.() || schedule.lead_assessor_id,
                });
                const newId = draftRes.data?.data?.id;
                if (newId) scheduleWithAsmt = { ...schedule, assessment_id: newId };
            }
            await exportSingleSchedule(scheduleWithAsmt);
            message.success(`Exported ${scheduleWithAsmt.facility_name} — ${scheduleWithAsmt.clause_code || 'Full Audit'}`);
            load();

        } catch (e) {
            message.error('Export failed: ' + e.message);
        } finally {
            setExportingRow(null);
        }

    };
    const handleBulkExport = async () => {
        const toExport = schedules.filter(s => selectedIds.has(s.id));
        if (!toExport.length) { message.warning('Select at least one schedule to export'); return; }
        setBulkExporting(true);
        let ok = 0;
        const failures = [];
        // Sequential, not parallel — triggering many simultaneous downloads
        // at once is more likely to get blocked by the browser's
        // multiple-download permission prompt than doing them one at a time.
        for (const s of toExport) {
            try { await exportSingleSchedule(s); ok++; }
            catch (e) { failures.push(`${s.facility_name} — ${s.clause_code || 'Full Audit'}: ${e.message}`); }
        }
        setBulkExporting(false);
        setSelectedIds(new Set());
        if (failures.length) {
            Modal.warning({
                title: `Exported ${ok} of ${toExport.length} — ${failures.length} failed`,
                width: 560,
                content: (
                    <div style={{ maxHeight:260, overflowY:'auto' }}>
                        {failures.map((f,i) => (
                            <div key={i} style={{ fontSize:12, padding:'6px 0', borderBottom: i<failures.length-1 ? '1px solid #f0f0f0' : 'none' }}>{f}</div>
                        ))}
                    </div>
                ),
            });
        } else {
            message.success(`Exported ${ok} assessment${ok!==1?'s':''} for offline work`);
        }
        load();
    };

    const toggleSelected = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // ── Bulk import: multiple completed files at once, each auto-routed to
    // its own schedule/assessment via the Meta sheet embedded at export time
    // — no need to have the matching modal open, or re-select facility/clause.
    const handleBulkImportFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        e.target.value = '';
        setImportingBulk(true);
        const submitted = [], draft = [], failed = [];

        for (const file of files) {
            try {
                // Empty context — deliberately skip the "does this match my
                // currently open modal" cross-check that the single-assessment
                // import path uses. There's no single modal in bulk mode; the
                // file's own embedded Meta sheet is the only source of truth
                // for where each import belongs.
                const { responses: imported, meta, errors } = await importAssessmentExcel(file, {});

                if (errors?.length) {
                    failed.push({ filename: file.name, reason: errors[0] });
                    continue;
                }
                if (!meta?.schedule_id) {
                    failed.push({ filename: file.name, reason: 'No schedule reference found in file — was this exported from this system?' });
                    continue;
                }
                const matched = schedules.find(s => String(s.id) === String(meta.schedule_id));
                if (!matched) {
                    failed.push({
                        filename: file.name,
                        reason: 'Matching schedule not found in the current list — it may be completed already, cancelled, or filtered out. Clear filters and try again.',
                    });
                    continue;
                }
                if (meta.clause_code && matched.clause_code &&
                    String(meta.clause_code).trim().toLowerCase() !== String(matched.clause_code).trim().toLowerCase()) {
                    failed.push({ filename: file.name, reason: `File is for clause ${meta.clause_code} but the matched schedule is ${matched.clause_code}` });
                    continue;
                }

                const mesRes = await QoCService.getApplicableMEs({ clause_code: matched.clause_code, facility_id: matched.facility_id });
                const mes = (mesRes.data?.data || []).filter(me => me.applicable !== 0);

                // Same completeness/scoring algorithm used by the single
                // assessment modal (recalculate()) — kept identical so a
                // bulk-imported file scores exactly the same as if someone
                // had typed the same answers in online.
                let C=0, D=0, E=0, NA=0;
                mes.forEach(me => {
                    const f = imported[me.id]?.fulfillment;
                    if      (f === 'Met')            C++;
                    else if (f === 'Partially Met')  D++;
                    else if (f === 'Not Met')        E++;
                    else if (f === 'Not Applicable') NA++;
                });
                const A = C + D + E;
                const answered = C + D + E + NA;
                const total    = mes.length;
                const isComplete = total > 0 && answered === total;
                // Score: only calculate when complete. Draft = 0 to avoid inflated scores
                const B_full = total > 0 ? 100 / total : 0;
                const correctScore = isComplete ? parseFloat(((C*B_full)+(D*B_full/2)).toFixed(2)) : 0;
                const respArray = mes.map(me => ({
                    me_id:                      me.id,
                    ref_no:                     me.ref_no,
                    fulfillment:                imported[me.id]?.fulfillment || '',
                    comment:                    imported[me.id]?.comment || '',
                    general_observation:        imported[me.id]?.general_observation || '',
                    criticality_of_observation: imported[me.id]?.criticality_of_observation || '',
                    area_of_good_practice:      imported[me.id]?.area_of_good_practice || '',
                    evidence_notes:             imported[me.id]?.evidence_notes || '',
                    checklist_state:            imported[me.id]?.checklist_state || [],
                }));

                const asmtData = {
                    facility_id:        matched.facility_id,
                    clause_id:          matched.clause_id,
                    schedule_id:        matched.id,
                    // Use the actual audit date embedded in the file's Meta
                    // sheet at export time — not "today" (the date this file
                    // happens to be re-imported), which was silently wrong
                    // for any late import. Falls back to today only if the
                    // file predates this field being recorded.
                    assessment_date:    meta.assessment_date || moment().format('YYYY-MM-DD'),
                    period_label:       resolvePeriodLabel(
                                            meta.assessment_date || moment().format('YYYY-MM-DD'),
                                            periodsList, matched?.period_label || null),
                    overall_percentage: isComplete ? correctScore : 0,
                    overall_score:      isComplete ? correctScore : 0,
                    status:             'draft',
                    assessor_id:        util.getUserId?.() || matched.lead_assessor_id,
                    ...(meta.assessment_id || matched.assessment_id ? { id: meta.assessment_id || matched.assessment_id } : {}),
                };
                const saveRes = await QoCService.saveAssessmentForSchedule(asmtData);
                const aId = saveRes.data?.data?.id || meta.assessment_id;
                await QoCService.saveAssessmentResponsesME(aId, respArray);

                // The file's been imported back — release the offline lock
                // so it no longer shows as "exported" on the list.
                if (aId) {
                    try { await QoCService.clearOfflineLock(aId); } catch { /* non-fatal */ }
                }

                const entry = { filename: file.name, facility_name: matched.facility_name,
                    clause_code: matched.clause_code, answered, total };
                // Always save as draft on import — assessor reviews and submits online
                draft.push(entry);




            } catch (err) {
                failed.push({ filename: file.name, reason: err.message || 'Import failed' });
            }
        }

        setImportingBulk(false);
        setImportResults({ submitted, draft, failed });
        load();
    };

    const load = async (overrideFilters) => {
        const af = overrideFilters || filters;
        setLoading(true);
        try {
            let res;
            // "Currently Exported" is a client-side concept (offline_locked_by
            // set), not a real status value the backend understands — never
            // send it through as an actual status filter.
            const isExportedFilter = af.status === '__exported__';
            if (myView && !isAdmin) {
                res = await QoCService.getSchedulesForAssessor();
            } else {
                res = await QoCService.getAuditSchedules({
                    ...(af.facility_id && { facility_id: af.facility_id }),
                    ...(af.region      && { region: af.region }),
                    ...(af.status && !isExportedFilter && { status: af.status }),
                    ...(af.clause_id   && { clause_id: af.clause_id }),
                });
            }
            let data = res.data?.data || [];
            // Completed schedules have been submitted for review and belong in
            // the Approval Queue, not here — this page is for capture, not
            // review. Keeping them here just clutters "what's still
            // outstanding" with things that are already done.
            data = data.filter(s => s.status !== 'cancelled' && s.status !== 'completed');
            // Applied client-side regardless of which endpoint supplied the
            // data — getSchedulesForAssessor() (the "My Assignments" path)
            // takes no filter params at all, so without this, the filter bar
            // would silently do nothing for non-admin users viewing their
            // own assignments.
            if (af.facility_id) data = data.filter(s => String(s.facility_id) === String(af.facility_id));
            if (af.region)      data = data.filter(s => s.region === af.region);
            if (af.clause_id)   data = data.filter(s => String(s.clause_id) === String(af.clause_id));
            if (af.status && !isExportedFilter) data = data.filter(s => s.status === af.status);
            if (isExportedFilter) data = data.filter(s => !!s.offline_locked_by);
            setSchedules(data);
        } catch { message.error('Failed to load assessments'); }
        finally   { setLoading(false); }
    };

    const grouped = schedules.reduce((acc, s) => {
        const key = s.facility_id;
        if (!acc[key]) acc[key] = {
            facility_name: s.facility_name, facility_id: key,
            site_type: s.site_type, region: s.region, schedules: [],
        };
        acc[key].schedules.push(s);
        return acc;
    }, {});

    // Facilities shown in the Facility filter dropdown — only ones that
    // actually have a schedule right now (not the full facility register),
    // scoped to whichever region is selected, with a count of how many
    // schedules each has. Computed from `grouped` (the already-loaded
    // schedule list) rather than a separate facilities-with-schedules API
    // call, since `grouped` already has exactly this information.
    const scheduledFacilityOptions = Object.values(grouped)
        .filter(fac => !filters.region || fac.region === filters.region)
        .map(fac => ({ id: fac.facility_id, business_name: fac.facility_name, count: fac.schedules.length }))
        .sort((a, b) => a.business_name.localeCompare(b.business_name));

    const getClauseProgress = (scheds) => {
        const total      = scheds.length;
        const draft      = scheds.filter(s => s.status === 'in_progress').length;
        // 'completed' never appears here anymore (submitted schedules move to
        // the Approval Queue), so "not started" — scheduled with no
        // assessment begun — is the more useful counterpart to "in draft".
        const notStarted = scheds.filter(s => s.status === 'scheduled' && !s.assessment_id).length;
        return { total, draft, notStarted, pct: total > 0 ? Math.round(draft/total*100) : 0 };
    };

    const today = moment().startOf('day');


    // ── Push single assessment to HIE ────────────────────────
    const pushToHie = async (schedule) => {
        if (!schedule.assessment_id) return;
        setHiePushing(p => ({ ...p, [schedule.id]: true }));
        try {
            await QoCService.pushAssessmentToHie(schedule.assessment_id);
            message.success((schedule.facility_name || 'Assessment') + ' pushed to HIE successfully');
            trailUpdate(TRAIL_MODULES.HIE_SYNC, 'HIE Push', schedule.assessment_id, { facilityName: schedule.facility_name, status: 'synced' });
            load();
        } catch (err) {
            const errMsg = err?.response?.data?.message || err.message;
            message.error('HIE push failed: ' + errMsg);
            trailUpdate(TRAIL_MODULES.HIE_SYNC, 'HIE Push Failed', schedule.assessment_id, { facilityName: schedule.facility_name, error: errMsg });
        }
        setHiePushing(p => ({ ...p, [schedule.id]: false }));
    };

    const pushAllToHie = async () => {
        setHiePushAll(true);
        try {
            const res = await QoCService.pushPendingAssessments();
            const d = res.data;
            message.success('HIE sync complete — ' + d.pushed + ' pushed, ' + d.failed + ' failed');
            load();
        } catch (err) {
            message.error('Bulk HIE push failed: ' + (err?.response?.data?.message || err.message));
        }
        setHiePushAll(false);
    };

    const clauseColumns = [
        {
            title:'', width:36,
            render:(_,s) => (
                s.offline_locked_by ? null : (
                    <input type="checkbox"
                        checked={selectedIds.has(s.id)}
                        onChange={() => toggleSelected(s.id)}
                        style={{ width:15, height:15, cursor:'pointer' }}/>
                )
            )
        },
        {
            title:'Clause', width:200,
            render:(_,s) => {
                const cc = s.clause_code, col = CLAUSE_COLORS[cc]||'#1890ff';
                return (
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:28, height:28, borderRadius:6, background:col,
                            color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:10, fontWeight:800, flexShrink:0 }}>
                            {cc||'ALL'}
                        </div>
                        <div>
                            <div style={{ fontWeight:700, fontSize:12, color:col }}>{cc||'Full Audit'}</div>
                            <div style={{ fontSize:10, color:'#888' }}>{(s.clause_title||'').slice(0,28)}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            title:'Scheduled Date', width:110,
            render:(_,s) => {
                const overdue = s.status!=='completed' && s.status!=='cancelled'
                    && moment(s.scheduled_date).isBefore(today);
                return (
                    <div>
                        <div style={{ fontWeight:600, fontSize:12, color:overdue?'#f5222d':'#333' }}>
                            {moment(s.scheduled_date).format('DD MMM YYYY')}
                        </div>
                        {overdue && (
                            <Tag color="red" style={{ fontSize:9 }}>
                                {today.diff(moment(s.scheduled_date),'days')}d overdue
                            </Tag>
                        )}
                        {s.window_end && (
                            <div style={{ fontSize:10, color:'#aaa' }}>
                                End: {moment(s.window_end).format('DD MMM YY')}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title:'Lead Assessor', dataIndex:'lead_assessor_name', width:130,
            render:v => v
                ? <span style={{ fontSize:12 }}><UserOutlined style={{ marginRight:4 }}/>{v}</span>
                : <span style={{ color:'#ccc' }}>Unassigned</span>
        },
        {
            title:'Status', width:130,
            render:(_,s) => {
                const isRevision = s.assessment_status === 'revision_requested';
                if (isRevision) return (
                    <div>
                        <Tag color="#fa8c16" style={{ fontWeight:700, fontSize:11 }}>🔄 In Progress</Tag>
                        <div style={{ fontSize:9, color:'#fa8c16', marginTop:2 }}>Revision required</div>
                    </div>
                );
                const cfg = STATUS_CFG[s.status] || STATUS_CFG.scheduled;
                return <Tag color={cfg.color} style={{ fontWeight:700, fontSize:11 }}>{cfg.icon} {cfg.label}</Tag>;
            }
        },
        {
            title:'Assessment', width:140,
            render:(_,s) => {
                if (!s.assessment_id) return <span style={{ color:'#ccc', fontSize:11 }}>Not started</span>;
                const r2 = getRating(parseFloat(s.assessment_score||s.overall_percentage||0));
                const isRevision = s.assessment_status === 'revision_requested';
                return (
                    <div>
                        {s.assessment_status === 'draft'
                            ? <span style={{ color:'#aaa', fontSize:12 }}>In progress</span>
                            : <><span style={{ fontWeight:800, color:r2.color, fontSize:13 }}>{parseFloat(s.assessment_score||s.overall_percentage||0).toFixed(1)}%</span><Stars count={r2.stars} size={10}/></>
                        }
                        <div style={{ fontSize:10, marginTop:2 }}>
                            {isRevision
                                ? <Tag color="orange" style={{ fontSize:9, padding:'0 4px' }}>✏ Revision Requested</Tag>
                                : <span style={{ color:'#888' }}>{s.assessment_status==='draft'?'📝 Draft':'✅ Submitted'}</span>
                            }
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'HIE', width: 80,
            render: (_, s) => {
                if (!s.assessment_id) return null;
                const hs = s.hie_status;
                if (hs === 'synced') return (
                    <Tag style={{ fontSize:10, margin:0 }} color="success">
                        <CheckCircleOutlined/> Synced
                    </Tag>
                );
                if (hs === 'error') return (
                    <Tooltip title={s.hie_error || 'Sync failed — click to retry'}>
                        <Tag style={{ fontSize:10, margin:0, cursor:'pointer' }} color="error"
                            onClick={() => pushToHie(s)}>
                            <CloseCircleOutlined/> Error
                        </Tag>
                    </Tooltip>
                );
                if (s.assessment_status === 'approved' || s.hie_status === 'pending') return (
                    <Button size="small"
                        style={{ fontSize:10, height:22, padding:'0 6px',
                                 borderColor:'#0ea5e9', color:'#0ea5e9' }}
                        loading={!!hiePushing[s.id]}
                        icon={<CloudUploadOutlined/>}
                        onClick={() => pushToHie(s)}>
                        Push
                    </Button>
                );
                return (
                    <Tag style={{ fontSize:10, margin:0 }} color="default">
                        <ClockCircleOutlined/> Pending
                    </Tag>
                );
            }
        },
        {
            title:'Offline', width:130,
            render:(_,s) => {
                if (!s.offline_locked_by) return null;
                return (
                    <Tooltip title={`Exported ${moment(s.offline_locked_at).format('DD MMM YYYY, HH:mm')}`}>
                        <Tag color="purple" style={{ fontSize:10, margin:0, whiteSpace:'normal' }}>
                            🔒 {s.offline_locked_by_name || 'Unknown'}
                            <div style={{ fontSize:9, fontWeight:400 }}>
                                {moment(s.offline_locked_at).format('DD MMM')}
                            </div>
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title:'', width:150,
            render:(_,s) => {
                const isRevision  = s.assessment_status === 'revision_requested';
                const canAssess   = (s.status!=='completed' && s.status!=='cancelled') || isRevision;
                const btnColor    = isRevision ? '#fa8c16' : (CLAUSE_COLORS[s.clause_code]||'#1890ff');
                const btnLabel    = isRevision ? '✏ Revise' : (s.assessment_id ? 'Continue' : 'Start');
                return (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {canAssess && (
                            <Button size="small" type="primary"
                                style={{ background:btnColor, borderColor:btnColor, fontSize:11 }}
                                onClick={() => { setSelected(s); setViewOnly(false); }}>
                                {btnLabel}
                            </Button>
                        )}
                        {s.assessment_id && (
                            <Button size="small" icon={<EyeOutlined/>}
                                onClick={() => { setSelected(s); setViewOnly(true); }}>
                                View
                            </Button>
                        )}
                        {!s.offline_locked_by && (
                            <Tooltip title="Export this assessment for offline capture">
                                <Button size="small" icon={<DownloadOutlined/>}
                                    loading={exportingRow === s.id}
                                    onClick={() => handleExportRow(s)}/>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Quality Assessments</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Clause-by-clause assessments based on assigned schedules
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {isAdmin && (
                        <Button icon={<CloudUploadOutlined/>} loading={hiePushAll}
                            onClick={pushAllToHie}
                            style={{ borderColor:'#0ea5e9', color:'#0ea5e9' }}>
                            Sync All to HIE
                        </Button>
                    )}
                    <InstructionsButton/>
                    {isAdmin && (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ fontSize:12, color:'#888' }}>View:</span>
                            <Button size="small" type={myView?'primary':'default'}
                                onClick={() => { setMyView(true); load(); }}>
                                My Assignments
                            </Button>
                            <Button size="small" type={!myView?'primary':'default'}
                                onClick={() => { setMyView(false); setTimeout(load,50); }}>
                                All
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="page-pad">
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col span={4}>
                            <Select allowClear placeholder="Region" style={{ width:'100%' }}
                                value={filters.region}
                                onChange={v => {
                                    const next = { ...filters, region: v };
                                    // Drop a facility selection that no longer matches the new region
                                    if (v && next.facility_id) {
                                        const stillMatches = scheduledFacilityOptions.some(fo => String(fo.id) === String(next.facility_id));
                                        if (!stillMatches) next.facility_id = null;
                                    }
                                    setFilters(next);
                                    load(next);
                                }}>
                                {[...new Set(schedules.map(s => s.region).filter(Boolean))].sort().map(r => (
                                    <Select.Option key={r} value={r}>{r}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select allowClear placeholder="Filter by Facility" style={{ width:'100%' }}
                                showSearch optionFilterProp="children"
                                value={filters.facility_id}
                                onChange={v => { const next = {...filters,facility_id:v}; setFilters(next); load(next); }}>
                                {scheduledFacilityOptions.map(f=>(
                                    <Select.Option key={f.id} value={f.id}>
                                        {f.business_name}
                                        <span style={{ color:'#aaa', marginLeft:6, fontSize:11 }}>
                                            ({f.count} schedule{f.count!==1?'s':''})
                                        </span>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select allowClear placeholder="Status" style={{ width:'100%' }}
                                value={filters.status}
                                onChange={v => { const next = {...filters,status:v}; setFilters(next); load(next); }}>
                                {Object.entries(STATUS_CFG)
                                    .filter(([k]) => k !== 'completed') // completed schedules are never listed here anymore
                                    .map(([k,v])=>(
                                        <Select.Option key={k} value={k}>{v.label}</Select.Option>
                                    ))}
                                <Select.Option value="__exported__">🔒 Currently Exported</Select.Option>
                            </Select>
                        </Col>
                        <Col span={5}>
                            <Select allowClear placeholder="Clause" style={{ width:'100%' }}
                                value={filters.clause_id}
                                onChange={v => { const next = {...filters,clause_id:v}; setFilters(next); load(next); }}>
                                {clauses.map(c=>(
                                    <Select.Option key={c.id} value={c.id}>
                                        {c.code} — {c.title?.slice(0,20)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={3}>
                            <Button loading={loading} onClick={() => {
                                const cleared = { facility_id:null, region:null, status:null, clause_id:null };
                                setFilters(cleared); load(cleared);
                            }}>Clear</Button>
                        </Col>
                    </Row>
                </Card>

                <Card size="small" style={{ marginBottom:16, background:'#fafafa' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:'#555' }}>
                            {selectedIds.size > 0
                                ? `${selectedIds.size} selected`
                                : 'Select assessments below to export them for offline capture'}
                        </span>
                        <Button size="small"
                            onClick={() => setSelectedIds(new Set(schedules.filter(s => !s.offline_locked_by).map(s => s.id)))}>
                            Select All{Object.keys(filters).some(k => filters[k]) ? ' (matching filters)' : ''}
                        </Button>
                        <Button size="small" type="primary" icon={<DownloadOutlined/>}
                            disabled={selectedIds.size === 0} loading={bulkExporting}
                            onClick={handleBulkExport}>
                            Export Selected
                        </Button>
                        {selectedIds.size > 0 && (
                            <Button size="small" onClick={() => setSelectedIds(new Set())}>Clear Selection</Button>
                        )}
                        <div style={{ marginLeft:'auto' }}>
                            <Button size="small" icon={<UploadOutlined/>}
                                onClick={() => { setImportResults(null); setImportOpen(true); }}>
                                Import Completed Assessments
                            </Button>
                        </div>
                    </div>
                </Card>

                {loading
                    ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
                    : Object.keys(grouped).length === 0
                        ? <Alert
                            message="No assigned clauses found"
                            description={myView
                                ? "You have no clauses assigned for assessment. Contact the national office to schedule clauses for your facility."
                                : "No schedules match the selected filters."}
                            type="info" showIcon/>
                        : Object.values(grouped).map(fac => {
                            const prog      = getClauseProgress(fac.schedules);
                            const progColor = prog.pct===100?'#52c41a':prog.pct>0?'#1890ff':'#d9d9d9';
                            const facUnlockedIds = fac.schedules.filter(s => !s.offline_locked_by).map(s => s.id);
                            const facAllSelected = facUnlockedIds.length > 0 && facUnlockedIds.every(id => selectedIds.has(id));
                            return (
                                <Card key={fac.facility_id}
                                    style={{ marginBottom:16, borderRadius:10, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}
                                    bodyStyle={{ padding:0 }}>
                                    <div style={{ padding:'14px 20px',
                                        background:'linear-gradient(135deg,#f0f5ff,#f8faff)',
                                        borderBottom:'1px solid #e8eeff',
                                        borderRadius:'10px 10px 0 0',
                                        display:'flex', alignItems:'center', gap:16 }}>
                                        {facUnlockedIds.length > 0 && (
                                            <input type="checkbox"
                                                checked={facAllSelected}
                                                onChange={() => setSelectedIds(prev => {
                                                    const next = new Set(prev);
                                                    facUnlockedIds.forEach(id => facAllSelected ? next.delete(id) : next.add(id));
                                                    return next;
                                                })}
                                                title="Select all clauses for this facility"
                                                style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }}/>
                                        )}
                                        <div style={{ flex:1 }}>
                                            <div style={{ fontWeight:800, fontSize:15, color:'#1a1a2e' }}>
                                                {fac.facility_name}
                                            </div>
                                            <div style={{ fontSize:11, color:'#888', marginTop:2 }}>
                                                {fac.site_type} • {fac.region}
                                                <span style={{ marginLeft:12 }}>
                                                    {fac.schedules.length} clause{fac.schedules.length!==1?'s':''} assigned
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display:'flex', alignItems:'center', gap:20 }}>
                                            <Button size="small" icon={<span role="img" aria-label="cap">📋</span>}
                                                onClick={() => setCapModalFacility({ id: fac.facility_id, business_name: fac.facility_name })}
                                                style={{ borderColor:'#fa8c16', color:'#fa8c16' }}>
                                                Corrective Action Plan
                                            </Button>
                                            <div style={{ textAlign:'center' }}>
                                                <div style={{ fontSize:10, color:'#888', fontWeight:700 }}>NOT STARTED</div>
                                                <div style={{ fontSize:20, fontWeight:900, color:'#bfbfbf' }}>{prog.notStarted}</div>
                                                <div style={{ fontSize:10, color:'#aaa' }}>of {prog.total}</div>
                                            </div>
                                            <div style={{ textAlign:'center' }}>
                                                <div style={{ fontSize:10, color:'#888', fontWeight:700 }}>IN DRAFT</div>
                                                <div style={{ fontSize:20, fontWeight:900, color:'#faad14' }}>{prog.draft}</div>
                                            </div>
                                            <div style={{ textAlign:'center', minWidth:90 }}>
                                                <Progress type="circle" percent={prog.pct} width={64}
                                                    strokeColor={progColor}
                                                    format={v=><span style={{ fontSize:13, fontWeight:900, color:progColor }}>{v}%</span>}/>
                                                <div style={{ fontSize:10, color:'#888', marginTop:3 }}>in draft</div>
                                            </div>
                                        </div>
                                    </div>
                                    <Table size="small" dataSource={fac.schedules} columns={clauseColumns}
                                        rowKey="id" pagination={false}
                                        style={{ borderRadius:'0 0 10px 10px' }}
                                        rowClassName={s => {
                                            if (s.status==='completed') return 'row-completed';
                                            if (s.status!=='completed' && moment(s.scheduled_date).isBefore(today)) return 'row-overdue';
                                            return '';
                                        }}/>
                                </Card>
                            );
                        })
                }
            </div>

            {selectedSchedule && (
                <ClauseAssessmentModal
                    schedule={selectedSchedule}
                    viewOnly={viewOnly}
                    onClose={() => setSelected(null)}
                    onSaved={() => { load(); }}
                />
            )}

            {capModalFacility && (
                <CapCommitmentModal
                    facility={capModalFacility}
                    onClose={() => setCapModalFacility(null)}
                    onSaved={() => setCapModalFacility(null)}
                />
            )}

            {importOpen && (
                <Modal
                    title={<span><UploadOutlined style={{ marginRight:8 }}/>Import Completed Assessments</span>}
                    open onCancel={() => setImportOpen(false)}
                    footer={<Button onClick={() => setImportOpen(false)}>Close</Button>}
                    width={640}>
                    {!importResults && (
                        <>
                            <Alert type="info" showIcon style={{ marginBottom:14 }}
                                message="Select every completed file at once"
                                description={
                                    <span>
                                        Each file is automatically matched back to its own facility and clause using
                                        the reference embedded when it was exported — you don't need to re-select
                                        anything. Fully answered assessments are submitted for review automatically;
                                        anything left incomplete is saved as a draft so you don't lose the work,
                                        and listed below so you know what still needs finishing.
                                    </span>
                                }/>
                            <input
                                ref={importBulkInputRef}
                                type="file"
                                multiple
                                accept=".xlsx"
                                style={{ display:'none' }}
                                onChange={handleBulkImportFiles}
                            />
                            <Button type="primary" icon={<UploadOutlined/>} loading={importing}
                                onClick={() => importBulkInputRef.current?.click()}>
                                {importing ? 'Importing…' : 'Select Files to Import'}
                            </Button>
                        </>
                    )}
                    {importResults && (
                        <div>
                            <Row gutter={12} style={{ marginBottom:16 }}>
                                <Col span={8}>
                                    <div style={{ textAlign:'center', padding:'10px 0', background:'#f6ffed', borderRadius:8, border:'1px solid #b7eb8f' }}>
                                        <div style={{ fontSize:24, fontWeight:900, color:'#52c41a' }}>{importResults.submitted.length}</div>
                                        <div style={{ fontSize:11, color:'#888' }}>Submitted for review</div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ textAlign:'center', padding:'10px 0', background:'#fffbe6', borderRadius:8, border:'1px solid #ffe58f' }}>
                                        <div style={{ fontSize:24, fontWeight:900, color:'#faad14' }}>{importResults.draft.length}</div>
                                        <div style={{ fontSize:11, color:'#888' }}>Saved as draft (incomplete)</div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ textAlign:'center', padding:'10px 0', background:'#fff2f0', borderRadius:8, border:'1px solid #ffccc7' }}>
                                        <div style={{ fontSize:24, fontWeight:900, color:'#f5222d' }}>{importResults.failed.length}</div>
                                        <div style={{ fontSize:11, color:'#888' }}>Failed to import</div>
                                    </div>
                                </Col>
                            </Row>

                            {importResults.draft.length > 0 && (
                                <div style={{ marginBottom:14 }}>
                                    <div style={{ fontWeight:700, fontSize:12, color:'#faad14', marginBottom:6 }}>
                                        ⚠ Saved as draft — still needs completing
                                    </div>
                                    {importResults.draft.map((d,i) => (
                                        <div key={i} style={{ fontSize:12, padding:'6px 10px', marginBottom:4,
                                            background:'#fffbe6', border:'1px solid #ffe58f', borderRadius:6 }}>
                                            <strong>{d.facility_name}</strong> — {d.clause_code || 'Full Audit'}
                                            <span style={{ color:'#888', marginLeft:8 }}>{d.answered} of {d.total} answered</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {importResults.submitted.length > 0 && (
                                <div style={{ marginBottom:14 }}>
                                    <div style={{ fontWeight:700, fontSize:12, color:'#52c41a', marginBottom:6 }}>
                                        ✓ Submitted for review
                                    </div>
                                    {importResults.submitted.map((d,i) => (
                                        <div key={i} style={{ fontSize:12, padding:'6px 10px', marginBottom:4,
                                            background:'#f6ffed', border:'1px solid #b7eb8f', borderRadius:6 }}>
                                            <strong>{d.facility_name}</strong> — {d.clause_code || 'Full Audit'}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {importResults.failed.length > 0 && (
                                <div>
                                    <div style={{ fontWeight:700, fontSize:12, color:'#f5222d', marginBottom:6 }}>
                                        ✗ Failed
                                    </div>
                                    {importResults.failed.map((d,i) => (
                                        <div key={i} style={{ fontSize:12, padding:'6px 10px', marginBottom:4,
                                            background:'#fff2f0', border:'1px solid #ffccc7', borderRadius:6 }}>
                                            <strong>{d.filename}</strong>
                                            <div style={{ color:'#888', fontSize:11 }}>{d.reason}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Button size="small" style={{ marginTop:10 }} onClick={() => setImportResults(null)}>
                                Import More Files
                            </Button>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}